import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../../lib/utils";

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  readonly title?: ReactNode;
  readonly headerRight?: ReactNode;
  readonly footer?: ReactNode;
  readonly tone?: "default" | "elevated";
}

/**
 * Surface container - used for every section / panel in the app.
 *
 * 2026-04-29 design pass - switched from hard 1px ring borders to
 * soft Stripe-style elevation tokens (``shadow-elev-1`` / ``-2``)
 * + the new ``--radius-xl`` (20px) bake. Every page that consumes
 * ``<Card>`` picks up the lift for free; brand cascade + light/dark
 * still flow through the existing semantic tokens.
 */
export function Card({
  title,
  headerRight,
  footer,
  tone = "default",
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl bg-surface",
        tone === "elevated" ? "shadow-elev-2" : "shadow-elev-1",
        className,
      )}
      {...rest}
    >
      {(title || headerRight) && (
        <header className="flex items-center justify-between gap-3 px-5 pb-3 pt-5 sm:px-6 sm:pt-6">
          {typeof title === "string" ? (
            <h2 className="text-base font-semibold tracking-tight text-strong">{title}</h2>
          ) : (
            title
          )}
          {headerRight}
        </header>
      )}
      <div className="px-5 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">{children}</div>
      {footer && (
        <footer className="border-t border-line bg-surface-muted px-5 py-3 sm:px-6">
          {footer}
        </footer>
      )}
    </section>
  );
}
